import { io } from 'socket.io-client';
import { API_BASE_URL } from 'utils/apiUtils';

class InsightsSocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.userId = null;
    this.connectionAttempts = 0;
    this.maxConnectionAttempts = 3;
    this.connecting = false;

    // Add timeout tracking for debounced events
    this.emitTimeouts = {};    // Refined listener tracking
    this.listeners = {
      statsUpdate: [],
      leadCaptured: [],
      cardView: [],
      linkTap: [],
      accessoryView: [],
      contactDownload: [],
      shareTracked: [],
      connect: [],
      disconnect: []
    };

    // Enhanced listener tracking with identifiers
    this.listenerTrackers = {
      statsUpdate: new Set(),
      leadCaptured: new Set(),
      cardView: new Set(),
      linkTap: new Set(),
      accessoryView: new Set(),
      contactDownload: new Set(),
      shareTracked: new Set(),
      connect: new Set(),
      disconnect: new Set()
    };

    // Bind methods to ensure correct context
    this.connect = this.connect.bind(this);
    this.disconnect = this.disconnect.bind(this);
    this.debouncedEmit = this.debouncedEmit.bind(this);
    this.emitNewLead = this.emitNewLead.bind(this);
    this.emitCardView = this.emitCardView.bind(this);
    this.emitLinkTap = this.emitLinkTap.bind(this);
    this.emitContactDownload = this.emitContactDownload.bind(this);
  }

  // Add debouncing for high-frequency events
  debouncedEmit(eventName, data, delay = 300) {
    if (this.emitTimeouts[eventName]) {
      clearTimeout(this.emitTimeouts[eventName]);
    }

    this.emitTimeouts[eventName] = setTimeout(() => {
      if (this.socket && this.connected) {
        console.log(`Emitting event ${eventName}:`, data);
        this.socket.emit(eventName, data);
      } else {
        console.warn(`Socket not connected for event ${eventName}`, data);
      }
      delete this.emitTimeouts[eventName];
    }, delay);
  }  // Enhanced connection method with retry logic
  connect(userId) {
    // Safety check for userId
    if (!userId) {
      console.error('Cannot connect to insights socket without a userId');
      return false;
    }

    // CRITICAL FIX: Check if we're already connected with this userId
    if (this.userId === userId && this.connected) {
      console.log(`Already connected for user: ${userId}, reusing connection`);
      return true;
    }

    // CRITICAL FIX: If we're connecting for a different user, disconnect first
    if (this.userId !== userId && (this.connected || this.connecting)) {
      console.log(`Currently connected to ${this.userId}, disconnecting before connecting to ${userId}`);
      this.disconnect();
    }

    // CRITICAL FIX: Always disconnect if already connecting, to prevent multiple connection attempts
    if (this.connecting) {
      console.log(`Already attempting to connect, resetting connection state`);
      this.disconnect();
    }

    // Reset connection attempts and set connecting state
    this.connectionAttempts = 0;
    this.connecting = true;
    this.userId = userId;

    console.log(`🔄 InsightsSocket: Connecting for user ${userId}`);

    // Attempt connection
    return this.initiateConnection();
  }
  // Internal connection method with retry mechanism
  initiateConnection() {
    if (this.connectionAttempts >= this.maxConnectionAttempts) {
      console.error('Max connection attempts reached');
      this.connecting = false;
      return false;
    }

    try {
      console.log(`Attempting to connect to socket (attempt ${this.connectionAttempts + 1}/${this.maxConnectionAttempts})`);

      // Force disconnect any existing socket before creating a new one
      if (this.socket) {
        try {
          this.socket.disconnect();
          this.socket.removeAllListeners();
        } catch (error) {
          console.warn('Error cleaning up existing socket during connection', error);
        }
        this.socket = null;
      }

      // Create socket connection
      this.socket = io(API_BASE_URL, {
        transports: ['websocket', 'polling'], // ✅ FIXED: Use WebSocket with polling fallback
        upgrade: true, // ✅ FIXED: Allow WebSocket upgrade
        rememberUpgrade: true, // ✅ FIXED: Remember successful upgrades
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: this.maxConnectionAttempts,
        query: { userId: this.userId }, // Add userId to query params
        // Enhanced WebSocket configuration
        forceNew: true,
        timeout: 20000,
        autoConnect: false // We'll connect manually
      });

      // Setup connection event handlers
      this.setupConnectionHandlers();

      // ✅ FIXED: Manually trigger connection since autoConnect is false
      this.socket.connect();

      this.connectionAttempts++;
      return true;
    } catch (error) {
      console.error('Socket connection error:', error);
      this.connecting = false;
      return false;
    }
  }

  // Comprehensive connection handlers
  setupConnectionHandlers() {
    if (!this.socket) return;

    // Connection success
    this.socket.on('connect', () => {
      console.log('🔌 Connected to insights socket:', {
        socketId: this.socket.id,
        transport: this.socket.io.engine.transport.name,
        protocol: this.socket.io.engine.protocol
      });
      this.connected = true;
      this.connecting = false;
      this.connectionAttempts = 0;

      // Join user-specific room
      if (this.userId) {
        this.socket.emit('joinInsightsRoom', this.userId, (response) => {
          if (response && response.success) {
            console.log('✅ Successfully joined insights room for user:', this.userId);
          } else {
            console.error('❌ Failed to join insights room:', response?.message);
          }
        });
      }

      // Trigger connect listeners
      this.triggerListeners('connect');
    });

    // Transport upgrade event
    this.socket.io.on('upgrade', () => {
      console.log('🚀 Socket transport upgraded to WebSocket');
    });

    // Transport error event
    this.socket.io.on('error', (error) => {
      console.error('❌ Socket transport error:', error);
    });

    // Disconnection handling
    this.socket.on('disconnect', (reason) => {
      console.log('🔌 Disconnected from insights socket:', {
        reason,
        transport: this.socket.io.engine.transport.name
      });
      this.connected = false;
      this.connecting = false;

      // Trigger disconnect listeners
      this.triggerListeners('disconnect');
    });

    // Connection error handling
    this.socket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', {
        error: error.message,
        transport: this.socket.io.engine.transport.name,
        attempts: this.connectionAttempts
      });

      // Attempt reconnection
      if (this.connectionAttempts < this.maxConnectionAttempts) {
        setTimeout(() => this.initiateConnection(), 1000);
      } else {
        this.connecting = false;
      }
    });

    // Setup specific event listeners
    this.setupListeners();
  }

  // Setup specific socket event listeners
  setupListeners() {
    if (!this.socket) return;

    this.socket.on('statsUpdate', (data) => {
      console.log('Received statsUpdate:', data);
      this.triggerListeners('statsUpdate', data);
    });

    this.socket.on('leadCaptured', (data) => {
      console.log('Received leadCaptured:', data);
      this.triggerListeners('leadCaptured', data);
    });

    this.socket.on('cardView', (data) => {
      console.log('Received cardView:', data);
      this.triggerListeners('cardView', data);
    });

    this.socket.on('linkTap', (data) => {
      console.log('Received linkTap:', data);
      this.triggerListeners('linkTap', data);
    });

    this.socket.on('accessoryView', (data) => {
      console.log('Received accessoryView:', data);
      this.triggerListeners('accessoryView', data);
    });

    this.socket.on('contactDownload', (data) => {
      console.log('Received contactDownload:', data);
      this.triggerListeners('contactDownload', data);
    });
  }

  // Generic listener triggering method
  triggerListeners(event, data) {
    if (this.listeners[event] && this.listeners[event].length > 0) {
      this.listeners[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in ${event} listener:`, error);
        }
      });
    }
  }  // Disconnect method with full cleanup
  disconnect() {
    console.log('🔌 InsightsSocket: Disconnecting and cleaning up...');

    // First clear all timeouts to prevent any pending operations
    Object.keys(this.emitTimeouts).forEach(key => {
      clearTimeout(this.emitTimeouts[key]);
      delete this.emitTimeouts[key];
    });

    if (this.socket) {
      console.log('Removing socket listeners and disconnecting');

      try {
        // Notify server we're leaving insights room if we have a userId
        if (this.userId && this.connected) {
          this.socket.emit('leaveInsightsRoom', this.userId);
        }

        // Remove all event listeners first
        this.socket.removeAllListeners();

        // Force disconnect with no reconnection attempts
        this.socket.io.opts.reconnection = false;
        this.socket.disconnect();
      } catch (error) {
        console.error('Error during socket disconnect:', error);
      }

      // Clear socket reference
      this.socket = null;
    }

    // Reset all connection state
    this.connected = false;
    this.connecting = false;
    this.userId = null;
    this.connectionAttempts = 0;

    // Clear listeners array but keep the structure
    Object.keys(this.listeners).forEach(event => {
      this.listeners[event] = [];
    });

    // Clear listener trackers
    Object.keys(this.listenerTrackers).forEach(event => {
      this.listenerTrackers[event].clear();
    });

    console.log('✅ InsightsSocket: Cleanup complete');
  }

  // Enhanced .on() method with unique identifier
  on(event, callback, identifier = null) {
    if (!this.listeners[event]) {
      console.warn(`Unrecognized event type: ${event}. Event listener not added.`);
      return this;
    }

    if (typeof callback === 'function') {
      this.listeners[event].push(callback);
      if (identifier) this.listenerTrackers[event].add(identifier);
    }

    return this;
  }

  // Enhanced .off() method to remove by identifier or callback
  off(event, callbackOrIdentifier) {
    if (!this.listeners[event]) {
      console.warn(`Unrecognized event type: ${event}. No listeners removed.`);
      return this;
    }

    if (typeof callbackOrIdentifier === 'function') {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callbackOrIdentifier);
    } else if (typeof callbackOrIdentifier === 'string') {
      this.listenerTrackers[event].delete(callbackOrIdentifier);
    }

    return this;
  }

  // Method to remove all listeners for a specific event type
  removeAllListeners(event) {
    if (this.listeners[event]) {
      this.listeners[event] = [];
      this.listenerTrackers[event].clear();
    }
    return this;
  }

  // Specific event listener methods
  onStatsUpdate(callback) {
    if (typeof callback === 'function') {
      this.listeners.statsUpdate.push(callback);
    }
    return this;
  }

  onLeadCaptured(callback) {
    if (typeof callback === 'function') {
      this.listeners.leadCaptured.push(callback);
    }
    return this;
  }

  onCardView(callback) {
    if (typeof callback === 'function') {
      this.listeners.cardView.push(callback);
    }
    return this;
  }

  onLinkTap(callback) {
    if (typeof callback === 'function') {
      this.listeners.linkTap.push(callback);
    }
    return this;
  }

  onAccessoryView(callback) {
    if (typeof callback === 'function') {
      this.listeners.accessoryView.push(callback);
    }
    return this;
  }

  onContactDownload(callback) {
    if (typeof callback === 'function') {
      this.listeners.contactDownload.push(callback);
    }
    return this;
  }

  // Specific listener removal methods
  offStatsUpdate(callback) {
    this.listeners.statsUpdate = this.listeners.statsUpdate.filter(cb => cb !== callback);
    return this;
  }

  offLeadCaptured(callback) {
    this.listeners.leadCaptured = this.listeners.leadCaptured.filter(cb => cb !== callback);
    return this;
  }

  offCardView(callback) {
    this.listeners.cardView = this.listeners.cardView.filter(cb => cb !== callback);
    return this;
  }

  offLinkTap(callback) {
    this.listeners.linkTap = this.listeners.linkTap.filter(cb => cb !== callback);
    return this;
  }

  offAccessoryView(callback) {
    this.listeners.accessoryView = this.listeners.accessoryView.filter(cb => cb !== callback);
    return this;
  }

  offContactDownload(callback) {
    this.listeners.contactDownload = this.listeners.contactDownload.filter(cb => cb !== callback);
    return this;
  }

  // Methods to emit events (with debouncing for high-frequency events)
  emitNewLead(leadData, callback) {
    if (!this.socket || !this.connected) {
      console.error('Socket not connected - attempting connection');
      if (leadData.userId) {
        this.connect(leadData.userId);
      }

      // Wait a bit for connection to establish and retry
      setTimeout(() => {
        if (this.connected) {
          console.log('Connection established, sending lead data');
          this.socket.emit('newLeadCaptured', leadData, callback);
        } else {
          console.error('Failed to connect socket to send lead data');
          callback && callback({ success: false, message: 'Socket not connected' });
        }
      }, 1000);
      return;
    }

    // Leads are important, so emit immediately
    console.log('Emitting newLeadCaptured event:', leadData);
    this.socket.emit('newLeadCaptured', leadData, callback);
  }

  emitCardView(viewData, callback) {
    if (!this.socket || !this.connected) {
      console.error('Socket not connected - attempting connection');
      if (viewData.userId) {
        this.connect(viewData.userId);
      }
      callback && callback({ success: false, message: 'Socket not connected' });
      return;
    }

    // Standardize property names
    const standardizedData = {
      ...viewData,
      sourceType: viewData.sourceType || viewData.viewSource || 'direct',
      timestamp: viewData.timestamp || new Date().toISOString()
    };

    // Log detailed information about the card view event
    console.log('Emitting cardViewed event:', {
      cardId: standardizedData.cardId,
      userId: standardizedData.userId,
      sourceType: standardizedData.sourceType,
      timestamp: standardizedData.timestamp
    });

    // Use debounced emit for high-frequency events
    this.debouncedEmit('cardViewed', standardizedData);

    // Still call the callback immediately
    if (callback) callback({ success: true });
  }

  emitLinkTap(tapData, callback) {
    if (!this.socket || !this.connected) {
      console.error('Socket not connected - attempting connection');
      if (tapData.userId) {
        this.connect(tapData.userId);
      }
      callback && callback({ success: false, message: 'Socket not connected' });
      return;
    }

    // Log detailed information about the link tap event
    console.log('Emitting linkTapped event:', {
      linkId: tapData.linkId,
      userId: tapData.userId,
      timestamp: new Date().toISOString()
    });

    // Use debounced emit for high-frequency events
    this.debouncedEmit('linkTapped', {
      ...tapData,
      timestamp: tapData.timestamp || new Date().toISOString()
    });

    // Still call the callback immediately
    if (callback) callback({ success: true });
  }

  emitAccessoryView(accessoryData, callback) {
    if (!this.socket || !this.connected) {
      console.error('Socket not connected - attempting connection');
      if (accessoryData.userId) {
        this.connect(accessoryData.userId);
      }
      callback && callback({ success: false, message: 'Socket not connected' });
      return;
    }

    // Log detailed information about the accessory view event
    console.log('Emitting accessoryViewed event:', {
      accessoryId: accessoryData.accessoryId,
      userId: accessoryData.userId,
      timestamp: new Date().toISOString()
    });

    // Use debounced emit for high-frequency events
    this.debouncedEmit('accessoryViewed', {
      ...accessoryData,
      timestamp: accessoryData.timestamp || new Date().toISOString()
    });

    // Still call the callback immediately
    if (callback) callback({ success: true });
  }

  emitContactDownload(downloadData, callback) {
    if (!this.socket || !this.connected) {
      console.error('Socket not connected - attempting connection');
      if (downloadData.userId) {
        this.connect(downloadData.userId);
      }

      // Wait a bit for connection to establish and retry
      setTimeout(() => {
        if (this.connected) {
          console.log('Connection established, sending contact download data');
          this.socket.emit('contactDownload', downloadData, callback);
        } else {
          console.error('Failed to connect socket to send contact download data');
          callback && callback({ success: false, message: 'Socket not connected' });
        }
      }, 1000);
      return;
    }

    // Log detailed information about the contact download event
    console.log('Emitting contactDownload event:', {
      cardId: downloadData.cardId,
      userId: downloadData.userId,
      timestamp: downloadData.timestamp || new Date().toISOString()
    });

    // Use debounced emit for high-frequency events
    this.debouncedEmit('contactDownload', {
      ...downloadData,
      timestamp: downloadData.timestamp || new Date().toISOString()
    });

    // Still call the callback immediately
    if (callback) callback({ success: true });
  }
}

// Create a singleton instance
const insightsSocket = new InsightsSocketService();
export default insightsSocket;